import { dataSyncValidator } from '../../utils/dataSyncValidator';
import { handleFulfilledInvalidation, isFulfilledAction, isRejectedAction, isFetchDataRejectedAction, } from './cacheInvalidationLogic';
import { add_note, update_note, delete_note } from '../note-slice';
import { create_lesson, update_lesson, delete_lesson } from '../lesson-slice';
import { update_tutor_availability } from '../schedule-slice';
import { updateTutorAvailability } from '../tutor-slice';
import { create_payment, create_subscription } from '../payment-slice';
import { updateStreak, awardBadge } from '../gamification-slice';
import { updateEngagementLevel } from '../engagement-slice';
import { update_student_settings as updateStudentSettingsStudent, update_student_address } from '../student-slice';
import { update_student_settings as updateStudentSettingsUser, update_tutor_settings, update_tutor_address } from '../user-slice';
import { updateTutorSettings } from '../tutor-slice';
/** Fulfilled action types per data type; built from action creators for type safety. */
const FULFILLED_TYPE_SETS = {
    auth: [
        updateStudentSettingsStudent.fulfilled.type,
        update_student_address.fulfilled.type,
        updateStudentSettingsUser.fulfilled.type,
        update_tutor_settings.fulfilled.type,
        update_tutor_address.fulfilled.type,
        updateTutorSettings.fulfilled.type,
    ],
    notes: [
        add_note.fulfilled.type,
        update_note.fulfilled.type,
        delete_note.fulfilled.type,
    ],
    lessons: [
        create_lesson.fulfilled.type,
        update_lesson.fulfilled.type,
        delete_lesson.fulfilled.type,
    ],
    schedule: [
        update_tutor_availability.fulfilled.type,
        updateTutorAvailability.fulfilled.type, // tutor-schedule uses tutor-slice for saves
    ],
    payments: [
        create_payment.fulfilled.type,
        create_subscription.fulfilled.type,
    ],
    gamification: [
        updateStreak.fulfilled.type,
        awardBadge.fulfilled.type,
        updateEngagementLevel.fulfilled.type,
    ],
};
/** Lazy-loaded persistence monitor to avoid circular dependency with store. */
let persistenceMonitorPromise = null;
function getPersistenceMonitor() {
    if (!persistenceMonitorPromise) {
        persistenceMonitorPromise = import('../../utils/persistenceMonitor').then((m) => m.persistenceMonitor);
    }
    return persistenceMonitorPromise;
}
/**
 * Cache Invalidation Middleware
 *
 * Runs after every dispatched action. On fulfilled save-like actions, calls
 * `dataSyncValidator.notifyStale(dataType)` for the related data type (auth, notes,
 * lessons, schedule, payments, gamification). That marks the type stale and notifies
 * listeners (e.g. PersistentDataProvider), which trigger refetch (e.g. refreshNotes(true))
 * so the UI stays in sync with the server. On rejected **mutation** actions, records the
 * failure in the persistence monitor. Rejected fetch/load thunks are skipped.
 *
 * Uses action creator .fulfilled.type so renames/removals cause compile-time errors.
 *
 * @see docs/DATA_FLOW_AND_PERSISTENCE.md
 * @see src/utils/dataSyncValidator.ts - notifyStale, addListener
 * @see src/components/common/PersistentDataProvider.tsx - Option B listener
 */
export const cacheInvalidationMiddleware = (store) => (next) => (action) => {
    const result = next(action);
    const a = action;
    if (isFulfilledAction(a) && a.type) {
        getPersistenceMonitor()
            .then((pm) => {
            try {
                handleFulfilledInvalidation(a, FULFILLED_TYPE_SETS, dataSyncValidator.notifyStale.bind(dataSyncValidator), pm.recordSaveSuccess.bind(pm));
            }
            catch (error) {
                console.error('Cache invalidation middleware error:', error);
                pm.recordSaveFailure(`Cache invalidation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        })
            .catch((err) => console.warn('Cache invalidation: persistence monitor not available', err));
    }
    if (isRejectedAction(a) && !isFetchDataRejectedAction(a)) {
        let errorMessage = 'Unknown error';
        if (a.payload) {
            if (typeof a.payload === 'string') {
                errorMessage = a.payload;
            }
            else if (typeof a.payload === 'object' && a.payload !== null) {
                const payload = a.payload;
                errorMessage = payload.message ?? payload.error ?? JSON.stringify(a.payload);
            }
            else {
                errorMessage = String(a.payload);
            }
        }
        const messageToRecord = errorMessage;
        getPersistenceMonitor()
            .then((pm) => pm.recordSaveFailure(messageToRecord))
            .catch((err) => console.warn('Cache invalidation: persistence monitor not available', err));
    }
    return result;
};
